import Reveal from '@/components/base/Reveal';
import { tradingSummary } from '@/utils/tradingSummary';
import { monthlyPerformance } from '@/utils/monthlyPerformance';

interface ReasonItem {
  index: string;
  icon: string;
  title: string;
  detail: string;
  statValue: string;
  statUnit: string;
  statLabel: string;
  emphasis?: boolean;
}

// Counted straight off the shared trade ledger, so the three reasons stay
// anchored to real figures instead of vague claims. Change the ledger and
// every number below moves with it.
const reasons: ReasonItem[] = [
  {
    index: '01',
    icon: 'ri-shield-check-line',
    title: 'Evidence, Not Claims',
    detail:
      'Saying a strategy works and showing what it actually did are two different things. Every entry is listed with its direction, date, entry, exit, volume and result, so the record sits underneath the claims rather than replacing them.',
    statValue: String(tradingSummary.totalTrades),
    statUnit: 'entries',
    statLabel: 'each carrying 6 logged fields — direction, date, entry, exit, volume, result',
  },
  {
    index: '02',
    icon: 'ri-filter-off-line',
    title: 'Nothing Filtered Out',
    detail:
      'Losing trades are kept in right alongside the winners. The journal is not trimmed down to the most flattering days, so it reflects the real sequence of outcomes instead of a hand-picked subset.',
    statValue: String(tradingSummary.lossTrades),
    statUnit: 'losing trades',
    statLabel: `left in alongside the ${tradingSummary.winTrades} winners — nothing removed for looking worse`,
    emphasis: true,
  },
  {
    index: '03',
    icon: 'ri-file-search-line',
    title: 'Open To Be Checked',
    detail:
      'Because the record is complete and unfiltered, it can be reviewed rather than taken on trust — counted, grouped and compared period by period. The reader reaches their own conclusion from the same data.',
    statValue: String(monthlyPerformance.length),
    statUnit: 'months',
    statLabel: `of unbroken history across ${tradingSummary.totalTrades} trades, open to be counted and compared`,
  },
];

function RecordPurpose() {
  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                WHY THE RECORD IS SHOWN
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px] max-w-[820px]">
                A Track Record Means Nothing If You Only See The Good Part.
              </h2>
            </div>
            <p className="text-[14px] leading-relaxed text-foreground-600 max-w-[420px] lg:pb-2">
              The journal is not marketing. It is the activity behind the system, kept whole so the
              results can be judged as they actually happened.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 rounded-lg border border-background-300 bg-background-100 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-background-200">
              {reasons.map((reason) => (
                <div key={reason.index} className="p-6 md:p-8 flex flex-col h-full">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-11 h-11 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-600">
                      <i className={`${reason.icon} text-xl`}></i>
                    </span>
                    <span className="font-data text-[13px] tracking-[0.22em] text-foreground-300">
                      {reason.index}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-foreground-950 text-[18px] md:text-[19px]">
                    {reason.title}
                  </h3>
                  <p className="mt-3 text-[13.5px] leading-relaxed text-foreground-600">
                    {reason.detail}
                  </p>
                  <div className="mt-6 pt-5 border-t border-background-200 flex items-end gap-2.5">
                    <span
                      className={`font-data font-bold leading-none text-[34px] ${
                        reason.emphasis ? 'text-accent-600' : 'text-primary-600'
                      }`}
                    >
                      {reason.statValue}
                    </span>
                    <span className="font-data text-[12px] tracking-[0.18em] uppercase text-foreground-500 pb-1">
                      {reason.statUnit}
                    </span>
                  </div>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-foreground-500">
                    {reason.statLabel}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={180}>
          <p className="mt-6 text-[13px] leading-relaxed text-foreground-500 max-w-[760px]">
            The purpose is not to impress — it is to be reviewable. The record is published so the
            same evidence behind every decision can be seen, questioned and checked.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default RecordPurpose;